import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';

import { AppButton, AppCheckbox, AppPagination, AppTable, AppTooltip } from '@/components/atoms';
import { PatientEditLink } from '@/components/molecules';
import BottomBar from '@/components/organisms/Footer';
import { formatDateWarekiWrap } from '@/components/organisms/RA2NDatePicker/datepickerLogic';
import { getAntdTableSortOrder } from '@/utils';
import useAppModal from '@/hooks/useAppModal';
import type {
  ConsentTreatmentPeriodPagination,
  ConsentTreatmentPeriodRow,
  ConsentTreatmentPeriodSortKey,
  SortDir,
} from '@/types/consentTreatmentPeriod.types';

/**
 * Cross-page selection state:
 *   mode === 'manual'    → `selected` is the explicit set of checked keys.
 *   mode === 'all'       → every row across ALL pages is considered checked,
 *                          except keys in `excluded`.
 */
export type SelectionState =
  | { mode: 'manual'; selected: Set<string> }
  | { mode: 'all'; excluded: Set<string> };

export interface ConsentTreatmentPeriodTableProps {
  rows: ConsentTreatmentPeriodRow[];
  pagination: ConsentTreatmentPeriodPagination | null;
  massagerLabel: string;
  buildEditConsentHref: (patientId: number, consentId: number) => string;
  onPageChange: (page: number) => void;

  /** Sort state (lifted — reset on new 検索). */
  sortField: ConsentTreatmentPeriodSortKey | null;
  sortOrder: SortDir | null;
  onSortChange: (field: ConsentTreatmentPeriodSortKey | null, order: SortDir | null) => void;

  /** Selection state (lifted — used by print/csv handlers). */
  selection: SelectionState;
  onSelectionChange: (next: SelectionState) => void;

  isLoading?: boolean;
  outputOption: boolean;
  onOutputOptionChange: (value: boolean) => void;
  onListPdf: () => void;
  onLabelPdf: () => void;
  onCsv: () => void;
  hasSearched: boolean;
}

const rowKey = (r: ConsentTreatmentPeriodRow) => `${r.consent_id}@${r.massager_id ?? 'null'}`;

const isChecked = (sel: SelectionState, key: string): boolean =>
  sel.mode === 'manual' ? sel.selected.has(key) : !sel.excluded.has(key);

const fmtWareki = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return formatDateWarekiWrap(d);
};

const truncate = (s: string | null, max = 20): string =>
  !s ? '' : s.length > max ? s.slice(0, max) + '…' : s;

const ConsentTreatmentPeriodTable: React.FC<ConsentTreatmentPeriodTableProps> = ({
  rows,
  pagination,
  massagerLabel,
  buildEditConsentHref,
  onPageChange,
  sortField,
  sortOrder,
  onSortChange,
  selection,
  onSelectionChange,
  isLoading = false,
  outputOption,
  onOutputOptionChange,
  onListPdf,
  onLabelPdf,
  onCsv,
  hasSearched,
}) => {
  const appModal = useAppModal();
  const allKeysOnPage = useMemo(() => rows.map(rowKey), [rows]);
  const allCheckedOnPage =
    allKeysOnPage.length > 0 && allKeysOnPage.every(k => isChecked(selection, k));

  const toggleAll = (checked: boolean) => {
    // Header 全選択 applies ACROSS all pages per BD spec.
    onSelectionChange(
      checked ? { mode: 'all', excluded: new Set() } : { mode: 'manual', selected: new Set() }
    );
  };

  const toggleRow = (key: string, checked: boolean) => {
    if (selection.mode === 'all') {
      const next = new Set(selection.excluded);
      if (checked) next.delete(key);
      else next.add(key);
      onSelectionChange({ mode: 'all', excluded: next });
    } else {
      const next = new Set(selection.selected);
      if (checked) next.add(key);
      else next.delete(key);
      onSelectionChange({ mode: 'manual', selected: next });
    }
  };

  const hasAnySelection = (): boolean =>
    selection.mode === 'all' ? true : selection.selected.size > 0;

  const guardAndRun = (action: () => void) => {
    if (!hasAnySelection()) {
      appModal.error({ title: '対象の行を選択してください。', okText: 'OK' });
      return;
    }
    action();
  };

  const columns: ColumnsType<ConsentTreatmentPeriodRow> = useMemo(
    () => [
      {
        title: (
          <AppCheckbox
            checked={allCheckedOnPage}
            onChange={(e: any) => toggleAll(!!e?.target?.checked)}
          />
        ),
        key: 'selection',
        width: 56,
        align: 'center',
        render: (_: unknown, record: ConsentTreatmentPeriodRow) => {
          const key = rowKey(record);
          return (
            <AppCheckbox
              checked={isChecked(selection, key)}
              onChange={(e: any) => toggleRow(key, !!e?.target?.checked)}
            />
          );
        },
      },
      {
        title: '受診者番号',
        key: 'patient_serial_number',
        width: 140,
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField ?? '', 'patient_serial_number', sortOrder ?? ''),
        render: (_: unknown, r: ConsentTreatmentPeriodRow) => (
          <PatientEditLink
            patientId={r.patient_id}
            target="_blank"
            value={r.patient_serial_number}
          />
        ),
      },
      {
        title: '受診者名',
        key: 'patient_name',
        width: 180,
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField ?? '', 'patient_name', sortOrder ?? ''),
        render: (_: unknown, r: ConsentTreatmentPeriodRow) => {
          const name = r.patient_name ?? '';
          return (
            <AppTooltip title={name} showOnlyOnOverflow>
              <div className="break-words line-clamp-2">{truncate(name, 20)}</div>
            </AppTooltip>
          );
        },
      },
      {
        title: '受診者名カナ',
        key: 'patient_name_kana',
        width: 180,
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField ?? '', 'patient_name_kana', sortOrder ?? ''),
        render: (_: unknown, r: ConsentTreatmentPeriodRow) => {
          const kana = r.patient_name_kana ?? '';
          return (
            <AppTooltip title={kana} showOnlyOnOverflow>
              <div className="break-words line-clamp-2">{truncate(kana, 20)}</div>
            </AppTooltip>
          );
        },
      },
      {
        title: '同意日',
        key: 'consent_date',
        width: 180,
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField ?? '', 'consent_date', sortOrder ?? ''),
        render: (_: unknown, r: ConsentTreatmentPeriodRow) => (
          <span className="whitespace-nowrap">
            <Link
              to={buildEditConsentHref(r.patient_id, r.consent_id)}
              target="_blank"
              className="mr-1"
            >
              <AppButton size="small">
                <i className="fa fa-file" />
              </AppButton>
            </Link>
            {fmtWareki(r.consent_date)}
          </span>
        ),
      },
      {
        title: '要加療期間開始日',
        key: 'treatment_term_from',
        width: 150,
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField ?? '', 'treatment_term_from', sortOrder ?? ''),
        render: (_: unknown, r: ConsentTreatmentPeriodRow) => (
          <span className="whitespace-nowrap">{fmtWareki(r.treatment_term_from)}</span>
        ),
      },
      {
        title: '要加療期間終了日',
        key: 'treatment_term_to',
        width: 150,
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField ?? '', 'treatment_term_to', sortOrder ?? ''),
        render: (_: unknown, r: ConsentTreatmentPeriodRow) => (
          <span className="whitespace-nowrap">{fmtWareki(r.treatment_term_to)}</span>
        ),
      },
      {
        title: massagerLabel,
        key: 'massager_name',
        width: 180,
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField ?? '', 'massager_name', sortOrder ?? ''),
        render: (_: unknown, r: ConsentTreatmentPeriodRow) => r.massager_name ?? '',
      },
    ],
    [allCheckedOnPage, selection, sortField, sortOrder, massagerLabel, buildEditConsentHref]
  );

  // Before any search: render nothing (BD spec "before search → card not visible").
  // Empty-after-search is handled by AppTable's built-in filtered empty message
  // ("該当する検索結果がありません。") via the `hasFilters` prop.
  if (!hasSearched) {
    return null;
  }

  const hasRows = rows.length > 0;

  // Match TreatmentRecordListPage convention: 該当件数: X 件 (total from
  // paginator.total — full query count, not current page's row count).
  const totalCount = pagination?.total ?? rows.length;
  const totalCountLabel = `該当件数: ${totalCount} 件`;

  return (
    <>
      <div className="mb-4">
        <div className="mb-2 text-sm text-gray-700">{totalCountLabel}</div>
        <div className="w-full max-w-full min-w-0 overflow-x-auto">
          <AppTable<ConsentTreatmentPeriodRow>
            columns={columns}
            dataSource={rows}
            rowKey={r => rowKey(r)}
            pagination={false}
            tableLayout="fixed"
            scroll={{ x: 1240 }}
            hasFilters={hasSearched}
            loading={isLoading}
            onChange={(_pagination, _filters, sorter) => {
              // BD §18 sort icon spec defines 3 states: ▲ (asc) / ▼ (desc) /
              // ⇅ (unsorted) — the ⇅ state means clear back to default order
              // (treatment_term_to ASC + serial_number ASC). Cycle: asc → desc → clear.
              const sort = Array.isArray(sorter) ? sorter[0] : sorter;
              const columnKey = sort?.columnKey
                ? (String(sort.columnKey) as ConsentTreatmentPeriodSortKey)
                : null;

              if (!columnKey) {
                onSortChange(null, null);
                return;
              }

              let nextField: ConsentTreatmentPeriodSortKey | null = columnKey;
              let nextOrder: SortDir | null;

              if (!sortField || sortField !== columnKey || !sortOrder) {
                nextOrder = 'asc';
              } else if (sortOrder === 'asc') {
                nextOrder = 'desc';
              } else {
                nextField = null;
                nextOrder = null;
              }

              onSortChange(nextField, nextOrder);
            }}
          />
        </div>

        {hasRows && pagination && pagination.last_page > 1 && (
          <div className="flex items-center justify-center mt-3 sm:justify-end">
            <AppPagination
              current={pagination.current_page}
              total={pagination.total}
              pageSize={pagination.per_page}
              onChange={onPageChange}
            />
          </div>
        )}
      </div>

      <BottomBar
        variant="action"
        visible={hasRows}
        fixed
        left={
          <div className="flex flex-col w-full gap-2 sm:w-auto">
            <AppCheckbox
              className="text-white"
              checked={outputOption}
              onChange={(e: any) => onOutputOptionChange(!!e?.target?.checked)}
            >
              <span className="text-white">病院情報印字</span>
            </AppCheckbox>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <AppButton
                className="w-full rounded sm:w-auto"
                onClick={() => guardAndRun(onListPdf)}
              >
                一覧印刷
              </AppButton>
              <AppButton
                className="w-full rounded sm:w-auto"
                onClick={() => guardAndRun(onLabelPdf)}
              >
                ラベル印刷
              </AppButton>
              <AppButton
                className="w-full rounded sm:w-auto"
                type="primary"
                onClick={() => guardAndRun(onCsv)}
              >
                CSV出力
              </AppButton>
            </div>
          </div>
        }
      />
    </>
  );
};

export default ConsentTreatmentPeriodTable;
